import { useState, useEffect, useRef } from 'react';
import api from '../../../../Axios.js';
import { toast } from 'react-toastify';
import { toastConfirm } from '../../../../utils/toastConfirm.jsx';
import * as XLSX from 'xlsx';
import { defaultDateFormatter, modalStyle, modalContentStyle } from '../../../../Helper/functionHelper.js';
import { CustomSelect } from '../../../../Helper/functionHelper.js';
import { FaSpinner } from 'react-icons/fa';
import useAuthUser from "../../../../hooks/useAuthUser.js";

const AssetMaker = () => {
    const { loggedInUser } = useAuthUser();
    const userRole = Number(loggedInUser?.role);

    const [assetMakers, setAssetMakers] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalData, setModalData] = useState(null);

    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [assetMakerFormData, setAssetMakerFormData] = useState({
        maker_name: '',
        status: 1,
        _id: null,
    });

    const [filteredAssetMakers, setFilteredAssetMakers] = useState([]);
    const [filterForm, setFilterForm] = useState({
        searchQuery: '',
        status: '',
    });
    const handleChangeFilter = (e) => {
        const { name, value } = e.target;
        setFilterForm({ ...filterForm, [name]: value });
    };
    useEffect(() => {
        const result = assetMakers.filter(item => {
            const status = item.status ?? '';
            const searchQuery = item.maker_name ?? '';

            const matchesStatus =
                filterForm.status === '' || status === Number(filterForm.status);

            const matchesQuery =
                !filterForm.searchQuery ||
                searchQuery.toLowerCase().includes(filterForm.searchQuery.toLowerCase());

            return matchesStatus && matchesQuery;
        });

        setFilteredAssetMakers(result);
    }, [filterForm, assetMakers]);
    useEffect(() => {
        setCurrentPage(1);
    }, [filterForm, itemsPerPage]);



    useEffect(() => {
        fetchAssetMakers();
    }, []);

    useEffect(() => {
        if (modalData) {
            setAssetMakerFormData({
                maker_name: modalData.maker_name || '',
                status: modalData.status ?? 1,
                _id: modalData._id || null,
            });
            setModalVisible(true);
        }
    }, [modalData]);

    const fetchAssetMakers = async () => {
        try {
            const res = await api.get('/asset-makers');
            setAssetMakers(res.data.data);
        } catch (err) {
            console.error(err);
        }
    };

    const deleteAssetMaker = (_id) => {
        toastConfirm({
            message: "Do you really want to delete this asset maker?",
            onConfirm: async () => {
                try {
                    await api.delete(`/asset-makers/${_id}`);
                    toast.success("Asset maker deleted successfully!");
                    fetchAssetMakers();
                } catch (err) {
                    // console.log(err.response.data.details)
                    toast.error(err?.response?.data?.details || err.message);
                }
            },
        });
    };

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentData = filteredAssetMakers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredAssetMakers.length / itemsPerPage);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const exportToExcel = () => {
        const exportData = filteredAssetMakers.map(item => ({
            "Asset Maker": item.maker_name,
            "Status": item.status ? 'Active' : 'In-Active',
            "Created By": item.created_by_name,
            "Created At": defaultDateFormatter(item.created_at),
            "Updated By": item.updated_by_name,
            "Updated At": defaultDateFormatter(item.updated_at),
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "AssetMakers");

        XLSX.writeFile(workbook, `Asset Makers Exported - ${new Date().toLocaleDateString()}.xlsx`);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setAssetMakerFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (assetMakerFormData._id) {
                await api.patch(`/asset-makers/${assetMakerFormData._id}`, assetMakerFormData);
                toast.success("Asset maker updated!");
            } else {
                await api.post(`/asset-makers`, assetMakerFormData);
                toast.success("Asset maker created!");
            }
            setModalVisible(false);
            fetchAssetMakers();
        } catch (err) {
            toast.error(err.response?.data?.error || 'An error occurred. Please try again.', {
                position: "top-right",
                autoClose: 3000,
            });
        }
    };

    // for import
    const [showImportBox, setShowImportBox] = useState(false);
    const [importBtnLabel, setImportBtnLabel] = useState('Import valid data');
    const fileInputRef = useRef(null);
    const importBoxRef = useRef(null);
    const importButtonRef = useRef(null);
    const [excelData, setExcelData] = useState([]);
    const [errors, setErrors] = useState([]);
    const [showModal, setShowModal] = useState(false);

    const handleImportClick = () => {
        setShowImportBox((prev) => !prev);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const workbook = XLSX.read(bstr, { type: 'binary' });
            const worksheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[worksheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false, dateNF: "yyyy-mm-dd" });

            const [validData, validationErrors] = validateData(jsonData);
            setExcelData(validData);
            setErrors(validationErrors);
            setShowModal(true);

            // hide import popups
            setShowImportBox((prev) => !prev);

        };

        reader.readAsBinaryString(file);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                importBoxRef.current &&
                !importBoxRef.current.contains(event.target) &&
                importButtonRef.current &&
                !importButtonRef.current.contains(event.target)
            ) {
                setShowImportBox(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const validateData = (data) => {
        const errors = [];

        const validData = data.map((row, index) => {
            const rowErrors = {};

            if (!row.maker_name) rowErrors.maker_name = "Asset Maker name is required";

            if (row.status && !['active', 'in-active'].includes(row.status.trim().toLowerCase())) {
                rowErrors.status = "Invalid status";
            }

            if (Object.keys(rowErrors).length > 0) {
                errors.push({ row: index + 2, errors: rowErrors }); // Excel rows typically start at 2
            }

            return row;
        });

        return [data, errors];
    };

    const handleImport = async () => {
        try {
            setImportBtnLabel(<FaSpinner />);
            // const validRows = excelData.filter((_, i) => !errors.some(e => e.row === i + 2));

            // if(validRows) {
            //     toast.error('No valid data found', {
            //         position: "top-right",
            //         autoClose: 3000,
            //     });
            // }

            const result = await api.post(`/asset-makers/import`, excelData, {
                headers: { 'Content-Type': 'application/json' }
            });
            setShowModal(false);
            setImportBtnLabel('Import valid data');
            toast.success(result.data.message);
            fetchAssetMakers();
        } catch (err) {
            toast.error(err.response?.data?.error || 'An error occurred. Please try again.', {
                position: "top-right",
                autoClose: 3000,
            });
        }
    };

    const handleDownloadSample = () => {
        const link = document.createElement('a');
        link.href = `${process.env.REACT_APP_BUILD_PATH}/uploads/sample_asset_maker_import.xlsx`;
        link.download = 'sample_asset_maker_import.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div>
            {modalVisible && (
                <div className="modal show d-block fade" tabIndex="-1" role="dialog" style={modalStyle} onClick={() => setModalVisible(false)}>
                    <div className="modal-dialog" role="document" style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{assetMakerFormData._id ? 'Edit Asset Maker' : 'Add Asset Maker'}</h5>
                                <button type="button" className="btn-close" onClick={() => setModalVisible(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="popup-form">
                                    <div className="popup-form-row form-row-full">
                                        <div className="row">
                                            <div className="form-info">
                                                <div className="form-info-label">Asset Maker*</div>
                                                <div className="form-info-content">
                                                    <input type="text" name="maker_name" value={assetMakerFormData.maker_name} onChange={handleChange}
                                                        className="form-control" placeholder="Asset Maker" required />
                                                </div>
                                            </div>
                                            <div className="form-info">
                                                <div className="form-info-label">Status*</div>
                                                <div className="form-info-content">
                                                    <CustomSelect
                                                        name="status"
                                                        value={assetMakerFormData.status}
                                                        options={[
                                                            { value: 1, label: 'Active' },
                                                            { value: 0, label: 'In-Active' },
                                                        ]}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pp-btn-flex">
                                                <button type="button" className="btn btn-success"
                                                    onClick={handleSave}>Save</button>
                                                <button type="button" className="btn btn-secondary" onClick={() => {
                                                    setModalVisible(false);
                                                    setModalData(null);
                                                }}>Cancel</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* import modal */}
            {showModal && (
                <div style={modalStyle}>
                    <div style={modalContentStyle}>
                        <h4>Preview Excel Data</h4>
                        <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Asset Maker Name</th>
                                        <th>Status</th>
                                        <th>Errors</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {excelData.map((row, idx) => {
                                        const rowError = errors.find(e => e.row === idx + 2);
                                        return (
                                            <tr key={idx} style={{ backgroundColor: rowError ? '#fdd' : '#dfd' }}>
                                                <td>{row.maker_name}</td>
                                                <td>{row.status}</td>
                                                <td style={{ color: 'red' }}>{rowError ? Object.values(rowError.errors).join(', ') : ''}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-3">
                            <button className="btn btn-success btn-sm me-2" onClick={handleImport}>{importBtnLabel}</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="row mt-3">
                <div className="col-lg-12">
                    <h2>
                        Asset Maker
                        <span className="edit float-end has-export">
                            {[1, 2, 8, 9].includes(userRole) && (
                                <span className="toggle-wrapper">
                                    <button
                                        type="button"
                                        id="importEmployees"
                                        className="btn btn-dark btn-sm toggle-btn"
                                        onClick={handleImportClick}
                                        ref={importButtonRef}
                                    >
                                        <span className="material-icons-outlined">file_download</span>
                                        <span className="text"> Import</span>
                                    </button>
                                    {showImportBox && (
                                        <div className="import-container me-2" style={{ "display": "block" }}>

                                            <div
                                                className="import-box import-section arrow-top"
                                                ref={importBoxRef}
                                                style={{ "display": "block" }}>
                                                <div className="upload-box" onClick={() => fileInputRef.current.click()}>
                                                    <h3 id="fileTrigger">
                                                        <span className="material-icons-outlined">file_download</span> Click here to import
                                                    </h3>
                                                    <div className="supported-files">Supported types: .xls and .csv</div>
                                                </div>

                                                <div className="form-info-content mt-3">
                                                    <input
                                                        className="form-control"
                                                        type="file"
                                                        accept=".xlsx, .xls"
                                                        ref={fileInputRef}
                                                        onClick={() => { fileInputRef.current.value = null; }}
                                                        onChange={handleFileChange}
                                                        style={{ display: 'none' }}
                                                    />
                                                    <button
                                                        className="btn btn-success mt-2"
                                                        onClick={handleDownloadSample}
                                                        rel="noopener noreferrer"
                                                    >
                                                        Download Excel Format
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </span>
                            )}

                            {/* Export button */}
                            <button
                                onClick={exportToExcel}
                                className="btn btn-success btn-sm ms-2"
                                disabled={filteredAssetMakers.length === 0}
                                style={{
                                    padding: "4px 12px",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    display: "inline-flex",
                                    alignItems: "center",
                                }}
                            >
                                <span
                                    className="icon"
                                    style={{
                                        fontSize: "15px",
                                        marginRight: "4px",
                                        position: "relative",
                                        top: "-1px",
                                    }}
                                >
                                    <img
                                        src={`${process.env.REACT_APP_PUBLIC_PATH}/images/icons/icon-export.svg`}
                                        style={{ height: "18px" }}
                                        alt="Export"
                                    />
                                </span>
                                <span className="text">Export</span>
                            </button>

                            {/* Add New Button */}
                            {[1, 2, 8, 9].includes(userRole) && (
                                <button title="Add New" className="btn add ms-2" onClick={() => setModalData({})}>
                                    <span className="material-icons-outlined">add_box</span>
                                </button>
                            )}
                        </span>
                    </h2>
                </div>
            </div>

            {/* Filters */}
            <div className="row">
                <div className="col-sm-2">
                    <label>Items per page:</label>
                    <select className='form-control' onChange={(e) => setItemsPerPage(parseInt(e.target.value))}>
                        <option value="5" selected={itemsPerPage === 5}>5</option>
                        <option value="10" selected={itemsPerPage === 10}>10</option>
                        <option value="25" selected={itemsPerPage === 25}>25</option>
                        <option value="50" selected={itemsPerPage === 50}>50</option>
                        <option value="100" selected={itemsPerPage === 100}>100</option>
                    </select>
                </div>
                <div className="col-sm-5">
                    <label>&nbsp;</label>
                    <input
                        type="text"
                        name="searchQuery"
                        placeholder="Search asset maker..."
                        value={filterForm.searchQuery}
                        onChange={handleChangeFilter}
                        className='form-control'
                    />
                </div>
                <div className="col-sm-5">
                    <label>&nbsp;</label>
                    <CustomSelect
                        name="status"
                        value={filterForm.status}
                        options={[
                            { value: '', label: 'All' },
                            { value: 1, label: 'Active' },
                            { value: 0, label: 'In-Active' },
                        ]}
                        onChange={handleChangeFilter}
                        disabled={false}
                        styles={{
                            menu: (provided) => ({
                                ...provided,
                                zIndex: 1100,  // apply zIndex to dropdown menu
                            }),
                        }}
                    />
                </div>
            </div>

            {/* Table */}
            <div className="row mt-3">
                <div className="col-12">
                    <div className="table-responsive tbl-fixed-header tbl-main">
                        <table id="lettertype-table" cellpadding="0" cellspacing="0" class="table table-col-freeze">
                            <thead>
                                <tr>
                                    <th>Asset Maker</th>
                                    <th>Status</th>
                                    <th class="last-col-freeze">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentData.map((maker, index) => (
                                    <tr key={index}>
                                        <td>{maker.maker_name}</td>
                                        <td>{maker.status ? 'Active' : 'In-Active'}</td>
                                        <td class="last-col-freeze">
                                            <button title="Edit" className='btn btn-sm' onClick={() => setModalData(maker)}>
                                                <span className="material-icons-outlined">edit</span>
                                            </button>
                                            <button title="Delete" className="btn btn-sm" onClick={() => deleteAssetMaker(maker._id)}>
                                                <span className="material-icons-outlined">delete</span>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredAssetMakers.length === 0 && <p className="text-center">No data found.</p>}
                    </div>
                </div>
            </div>

            {/* Pagination */}
            <div className="row align-items-center">
                <div className="col-sm-6 text-start">
                    {totalPages > 1 && (
                        <nav aria-label="Page navigation">
                            <ul className="pagination">

                                {/* First */}
                                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                    <button className="page-link btn btn-sm" onClick={() => paginate(1)}>
                                        ≪
                                    </button>
                                </li>

                                {/* Previous */}
                                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                    <button className="page-link btn btn-sm" onClick={() => paginate(currentPage - 1)}>
                                        ‹
                                    </button>
                                </li>

                                {/* Limited Page Numbers */}
                                {(() => {
                                    const pageNumbers = [];
                                    const maxVisible = 3; // show 3 pages at a time
                                    let start = Math.max(1, currentPage - 1);
                                    let end = Math.min(totalPages, start + maxVisible - 1);

                                    if (end - start < maxVisible - 1) {
                                        start = Math.max(1, end - maxVisible + 1);
                                    }

                                    for (let i = start; i <= end; i++) {
                                        pageNumbers.push(
                                            <li
                                                key={i}
                                                className={`page-item ${currentPage === i ? "active" : ""}`}
                                            >
                                                <button className="page-link btn btn-sm" onClick={() => paginate(i)}>
                                                    {i}
                                                </button>
                                            </li>
                                        );
                                    }
                                    return pageNumbers;
                                })()}

                                {/* Next */}
                                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                    <button className="page-link btn btn-sm" onClick={() => paginate(currentPage + 1)}>
                                        ›
                                    </button>
                                </li>

                                {/* Last */}
                                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                    <button className="page-link btn btn-sm" onClick={() => paginate(totalPages)}>
                                        ≫
                                    </button>
                                </li>

                            </ul>
                        </nav>
                    )}
                </div>

                <div className="col-sm-6 text-end">
                    Showing {filteredAssetMakers.length > indexOfFirstItem ? indexOfFirstItem + 1 : 0}
                    {" "}to{" "}
                    {filteredAssetMakers.length > indexOfLastItem ? indexOfLastItem : filteredAssetMakers.length}
                    {" "}of {filteredAssetMakers.length} ({totalPages} Pages)
                </div>
            </div>



        </div>
    );
};

export default AssetMaker;